import React, { useState, useEffect } from 'react';
import { useBioData } from '../../context/BioDataContext';
import './DeferredEligibilityChecker.css';

const DeferredEligibilityChecker = ({ scenario, onStatusChange }) => {
  const { bioData } = useBioData();
  const [eligibilityStatus, setEligibilityStatus] = useState({
    isEligible: false,
    hasMinimumService: false,
    minimumRetirementAge: null,
    earliestStartAge: null,
    fullBenefitAge: 62,
    yearsOfService: 0,
    messages: []
  });

  // Calculate Minimum Retirement Age (MRA) based on birth year
  const calculateMRA = (birthYear) => {
    if (birthYear < 1948) return 55;
    if (birthYear >= 1948 && birthYear <= 1952) return 55 + (birthYear - 1947) * 2 / 12;
    if (birthYear >= 1953 && birthYear <= 1964) return 56;
    if (birthYear >= 1965 && birthYear <= 1969) return 56 + (birthYear - 1964) * 2 / 12;
    return 57; // 1970 and later
  };

  useEffect(() => {
    if (!bioData.dateOfBirth || !bioData.entryOnDuty || !scenario.departureYear) {
      const newStatus = {
        isEligible: false,
        messages: ['Please provide birth date, entry on duty date, and departure year.']
      };
      setEligibilityStatus(prev => ({
        ...prev,
        ...newStatus
      }));
      if (onStatusChange) onStatusChange(newStatus);
      return;
    }

    const birthDate = new Date(bioData.dateOfBirth);
    const eodDate = new Date(bioData.entryOnDuty);
    const birthYear = birthDate.getFullYear();
    const departureAge = scenario.departureYear - birthYear;
    
    // Calculate years of service at departure
    const yearsOfService = scenario.departureYear - eodDate.getFullYear();
    
    // Calculate MRA
    const mra = calculateMRA(birthYear);
    
    // Determine earliest start age
    const earliestStartAge = Math.max(mra, 57); // FERS deferred can't start before 57 or MRA
    
    // Check minimum service requirement
    const hasMinimumService = yearsOfService >= 5;
    
    // Check if eligible
    const isEligible = hasMinimumService && departureAge >= 0;
    
    // Generate messages
    const messages = [];
    
    if (!hasMinimumService) {
      messages.push(`Need at least 5 years of service. You have ${yearsOfService.toFixed(1)} years.`);
    }
    
    if (isEligible) {
      messages.push(`✓ Eligible for deferred retirement with ${yearsOfService.toFixed(1)} years of service.`);
      messages.push(`✓ Can start benefits as early as age ${earliestStartAge}.`);
      messages.push(`✓ Full benefits (no reduction) starting at age 62.`);
      
      if (earliestStartAge < 62) {
        const maxReduction = (62 - earliestStartAge) * 5;
        messages.push(`⚠️ Starting before age 62 reduces benefits by up to ${maxReduction}%.`);
      }
    }

    const newStatus = {
      isEligible,
      hasMinimumService,
      minimumRetirementAge: mra,
      earliestStartAge,
      fullBenefitAge: 62,
      yearsOfService,
      messages
    };
    
    setEligibilityStatus(newStatus);
    
    // Call the callback to update the parent component
    if (onStatusChange) onStatusChange(newStatus);
    
    // (Already handled above)

  }, [bioData.dateOfBirth, bioData.entryOnDuty, scenario.departureYear, onStatusChange]);

  return (
    <div className="deferred-eligibility-checker">
      <h4>Deferred Retirement Eligibility</h4>
      
      <div className="eligibility-summary">
        <div className={`status-indicator ${eligibilityStatus.isEligible ? 'eligible' : 'not-eligible'}`}>
          <span className="status-icon">
            {eligibilityStatus.isEligible ? '✓' : '⚠️'}
          </span>
          <span className="status-text">
            {eligibilityStatus.isEligible ? 'Eligible for Deferred Benefits' : 'Not Eligible for Deferred Benefits'}
          </span>
        </div>
      </div>

      <div className="eligibility-details">
        <div className="detail-row">
          <span className="label">Years of Service:</span>
          <span className="value">{eligibilityStatus.yearsOfService.toFixed(1)}</span>
        </div>
        
        {eligibilityStatus.isEligible && (
          <>
            <div className="detail-row">
              <span className="label">Earliest Start Age:</span>
              <span className="value">{eligibilityStatus.earliestStartAge}</span>
            </div>
            <div className="detail-row">
              <span className="label">Full Benefit Age:</span>
              <span className="value">{eligibilityStatus.fullBenefitAge}</span>
            </div>
            <div className="detail-row">
              <span className="label">Minimum Retirement Age:</span>
              <span className="value">{eligibilityStatus.minimumRetirementAge?.toFixed(1)}</span>
            </div>
          </>
        )}
      </div>

      <div className="eligibility-messages">
        {eligibilityStatus.messages.map((message, index) => (
          <div key={index} className={`message ${message.startsWith('✓') ? 'success' : message.startsWith('⚠️') ? 'warning' : 'info'}`}>
            {message}
          </div>
        ))}
      </div>
    </div>
  );
};

export default DeferredEligibilityChecker;
